import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, FolderOpen, Package, Users, LogOut, Globe, Menu, X, ChevronLeft, List } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useState } from 'react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e2d19eae-21d9-4f5b-a36f-57b16cd8c13e/artifacts/91mwiq27_Aljabr-Laundry-Main-Logo-.png";

export default function AdminLayout() {
  const { t, toggleLanguage, isRTL } = useLanguage();
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: t('dashboard'), exact: true },
    { path: '/admin/price-lists', icon: List, label: t('priceLists') },
    { path: '/admin/categories', icon: FolderOpen, label: t('manageCategories') },
    { path: '/admin/products', icon: Package, label: t('manageProducts') },
  ];
  if (isAdmin) {
    navItems.push({ path: '/admin/users', icon: Users, label: t('manageUsers') });
  }

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 ${isRTL ? 'right-0' : 'left-0'} z-50 w-64 bg-white border-e border-slate-100 shadow-lg lg:shadow-none transform transition-transform lg:transform-none ${
          sidebarOpen ? 'translate-x-0' : isRTL ? 'translate-x-full lg:translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2">
                <img src={LOGO_URL} alt="Aljabr" className="h-8" />
                <span className="text-sm font-bold text-[#0056A6]">
                  {isRTL ? 'لوحة التحكم' : 'Admin'}
                </span>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden"
                onClick={() => setSidebarOpen(false)}
                data-testid="close-sidebar-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Nav Items */}
          <nav className="flex-1 p-3 space-y-1">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.path, item.exact)
                    ? 'bg-[#0056A6] text-white shadow-md shadow-blue-500/20'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-[#0056A6]'
                }`}
                data-testid={`nav-${item.path.split('/').pop() || 'dashboard'}`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Sidebar Footer */}
          <div className="p-3 border-t border-slate-100 space-y-2">
            <Link to="/" className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-[#0056A6] rounded-lg hover:bg-slate-50 transition-colors">
              <ChevronLeft className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
              {t('backToMenu')}
            </Link>
            <div className="px-3 py-2 bg-slate-50 rounded-lg">
              <p className="text-xs font-medium text-slate-700 truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-100 h-14 flex items-center px-4 gap-3" data-testid="admin-header">
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid="open-sidebar-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>

          <div className="flex-1" />

          <Button
            variant="ghost"
            size="sm"
            onClick={toggleLanguage}
            className="text-slate-600"
            data-testid="admin-language-switcher"
          >
            <Globe className="h-4 w-4 me-1" />
            {t('language')}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-slate-500 hover:text-red-500"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 me-1" />
            {t('logout')}
          </Button>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
